import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import styles from './theme-search-card.module.scss';

export interface ThemeSearchCardProps {
  /** The aspect ratio to use for the image */
  aspectRatio?: Content.Components['AspectRatio'];
  /** The class name to apply */
  className?: string;
  /** The text to show as a headline */
  headline?: Content.Components['SearchByThemeCardDto']['headline'];
  /** The url of the image to show */
  image: Content.Components['SearchByThemeCardDto']['image'];
  /** The link to show */
  link?: Content.Components['SearchByThemeCardDto']['link'];
  /** The splash to show on the card */
  splash?: Content.Components['SearchByThemeCardDto']['splash'];
  /** The text to show as a description */
  text?: Content.Components['SearchByThemeCardDto']['text'];
}

const ThemeSearchCard = ({
  aspectRatio,
  className,
  headline,
  image,
  link,
  splash,
  text,
}: ThemeSearchCardProps) => {
  const { t } = useI18n();
  const { bindRef, isHovered } = useIsHovered<HTMLElement>();

  const renderContent = () => (
    <React.Fragment>
      <div className={styles.imageWrapper}>
        <Image
          alt={image.altText}
          aspectRatio={aspectRatio}
          src={image.url}
          srcSet={{
            desktop: {
              width: 310,
            },
            laptop: {
              width: 297,
            },
            tablet: {
              width: 343,
            },
          }}
          unoptimized
          width={341}
        />

        {splash && (
          <div className={styles.splash}>
            {splash.primaryHeadline && (
              <Typography tagStyle="headingSmall">
                {splash.primaryHeadline}
              </Typography>
            )}

            {splash.secondaryHeadline && (
              <Typography tagStyle="bodySmall">
                {splash.secondaryHeadline}
              </Typography>
            )}
          </div>
        )}
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <Typography
            className={styles.headline}
            tag="h3"
            tagStyle="headingLarge"
          >
            {headline}
          </Typography>

          <Typography tag="p" tagStyle="bodyMedium">
            {text}
          </Typography>
        </div>

        <AnchorLink
          aria-label={t('items.common.words.showMore')}
          className={styles.link}
          hovered={isHovered}
          icon="ArrowRight"
          tabIndex={-1}
          text={t('items.common.words.showMore')}
        />
      </div>
    </React.Fragment>
  );

  if (link && link.url) {
    return (
      <Link
        ref={bindRef}
        className={classNames(styles.component, className)}
        url={link.url}
      >
        {renderContent()}
      </Link>
    );
  }

  return (
    <div ref={bindRef} className={classNames(styles.component, className)}>
      {renderContent()}
    </div>
  );
};

ThemeSearchCard.displayName = 'ThemeSearchCard';

export default ThemeSearchCard;
